import * as amplitude from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import type { ReactNode } from "react";

declare const process: { env: Record<string, string | undefined> };

const AMPLITUDE_SESSION_REPLAY_ID_PROPERTY = "[Amplitude] Session Replay ID";

interface SessionReplayPluginWithReplayProperties {
  getSessionReplayProperties?: () => Record<string, unknown> | undefined;
}

type SessionReplayTracking = Parameters<typeof amplitude.add>[0] &
  SessionReplayPluginWithReplayProperties;

declare global {
  interface Window {
    __redoOnboardingAmplitudeInitialized__?: boolean;
    __redoOnboardingAmplitudeSessionReplayTracking__?: SessionReplayTracking;
  }
}

const sessionReplayTracking: SessionReplayTracking =
  typeof window !== "undefined" &&
  window.__redoOnboardingAmplitudeSessionReplayTracking__
    ? window.__redoOnboardingAmplitudeSessionReplayTracking__
    : // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      (sessionReplayPlugin({
        forceSessionTracking: true,
        sampleRate: 1,
      }) as SessionReplayTracking);

if (typeof window !== "undefined") {
  window.__redoOnboardingAmplitudeSessionReplayTracking__ =
    sessionReplayTracking;
}

export let amplitudeEnabled = false;

if (
  typeof window !== "undefined" &&
  globalThis.AMPLITUDE_API_KEY &&
  !window.__redoOnboardingAmplitudeInitialized__
) {
  // Always mark as attempted so HMR reloads don't retry on failure.
  window.__redoOnboardingAmplitudeInitialized__ = true;
  try {
    amplitude.add(sessionReplayTracking);
    amplitude.init(globalThis.AMPLITUDE_API_KEY, {
      autocapture: {
        pageViews: true,
        sessions: true,
        formInteractions: true,
        fileDownloads: true,
        attribution: true,
        elementInteractions: {
          cssSelectorAllowlist: ["[data-track-id]"],
          actionClickAllowlist: ["[data-track-id]"],
          viewportContentUpdated: { enabled: false },
          dataAttributePrefix: "data-track-",
        },
      },
    });
    amplitudeEnabled = true;
  } catch {
    // Initialization failed (e.g. blocked by browser extension). Replay
    // backfill will be skipped gracefully.
  }
}

export function readAmplitudeReplayId(): string | undefined {
  const replayProperties = sessionReplayTracking.getSessionReplayProperties?.();
  const replayId = replayProperties?.[AMPLITUDE_SESSION_REPLAY_ID_PROPERTY];

  return typeof replayId === "string" && replayId.trim()
    ? replayId.trim()
    : undefined;
}

export function AmplitudeProvider({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
